import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CompanyHeader from '@/components/company/CompanyHeader';
import useOrgChartState, { IOrgChart } from '@/states/organization.state';
import useOrganizationListState from '@/states/organization-list.state';
import OrgChartForm from '@/components/company/organization/OrgChartForm';
import OrgChartTree from '@/components/company/organization/OrgChartTree';
import OrgChartDetails from '@/components/company/organization/OrgChartDetails';
import { useOrgChartHandler } from '@/hooks/useOrgChartHandler';
import { useOrgChartInit } from '@/hooks/useOrgChartInit';
import { Columns2, Maximize, Minimize } from 'lucide-react';
import classNames from 'classnames';
import useTenantState from '@/states/tenant.state';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import { env } from '@/utils/constants/env';

export default function OrganizationStructure() {
  const { organizationId } = useParams();
  const { treeData, isLoading, setTreeData } = useOrgChartInit(organizationId ? Number(organizationId) : undefined);
  const [selectedNode, setSelectedNode] = useState<IOrgChart | null>(null);
  const [formMode, setFormMode] = useState<'add' | 'edit' | 'add-child' | null>(null);
  const [viewMode, setViewMode] = useState<'split' | 'diagram' | 'form'>('split');
  const [autoFocusForm, setAutoFocusForm] = useState(false);
  const positionCounter = useRef(1);

  const [isPublished, setIsPublished] = useState(false);
  const [orgName, setOrgName] = useState<string>('');
  const { fetchOrganization } = useOrganizationListState();
  const { setSelectedOrganizationId } = useOrgChartState();

  useEffect(() => {
    if (organizationId) {
      setSelectedOrganizationId(Number(organizationId));
    } else {
      setSelectedOrganizationId(null);
    }
  }, [organizationId]);

  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (organizationId) {
        const org = await fetchOrganization(Number(organizationId));
        if (org) {
          setOrgName(org.name);
          setIsPublished(org.is_published);
        }
      }
    };
    fetchOrgDetails();
  }, [organizationId]);

  const { handleSave, transformToD3Tree } = useOrgChartHandler({
    setIsLoading: () => { }, // eslint-disable-line @typescript-eslint/no-empty-function
    setTreeData,
    setFormMode,
    setSelectedNode,
    organizationId: organizationId ? Number(organizationId) : undefined,
  });

  const handleExport = (format: string) => {
    if (format === 'pdf') {
      handleDownloadPdf();
    } else if (format === 'word') {
      handleDownloadWord();
    }
  };

  const { currentTenant } = useTenantState();


  const orgChartRef = useRef<HTMLDivElement>(null);
  const [isExporting] = useState(false);

  // Canvas-based org chart image generation
  const generateOrgChartImage = (treeNode: any): string => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Failed to get canvas context");
      return "";
    }

    const scale = 2;
    const boxWidth = 180;
    const boxHeight = 45;
    const horizontalSpacing = 40;
    const verticalSpacing = 80;
    const padding = 60;
    const borderRadius = 8;

    // Calculate tree dimensions
    const getTreeDimensions = (node: any, depth = 0): { width: number; height: number; maxDepth: number } => {
      if (!node.children || node.children.length === 0) {
        return { width: boxWidth, height: boxHeight, maxDepth: depth };
      }

      let totalChildrenWidth = 0;
      let maxChildDepth = depth;

      node.children.forEach((child: any, index: number) => {
        const childDim = getTreeDimensions(child, depth + 1);
        totalChildrenWidth += childDim.width;
        if (index < node.children.length - 1) {
          totalChildrenWidth += horizontalSpacing;
        }
        maxChildDepth = Math.max(maxChildDepth, childDim.maxDepth);
      });

      return {
        width: Math.max(boxWidth, totalChildrenWidth),
        height: (maxChildDepth - depth + 1) * (boxHeight + verticalSpacing),
        maxDepth: maxChildDepth
      };
    };

    const treeDim = getTreeDimensions(treeNode);
    const canvasWidth = treeDim.width + padding * 2;
    const canvasHeight = (treeDim.maxDepth + 1) * (boxHeight + verticalSpacing) + padding * 2;

    canvas.width = canvasWidth * scale;
    canvas.height = canvasHeight * scale;
    ctx.scale(scale, scale);

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw rounded rectangle helper
    const drawRoundedRect = (x: number, y: number, w: number, h: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    // Calculate node positions
    const positions: Map<any, { x: number; y: number }> = new Map();

    const calculatePositions = (node: any, depth: number, leftX: number): number => {
      const nodeDim = getTreeDimensions(node, depth);

      if (!node.children || node.children.length === 0) {
        const x = leftX + nodeDim.width / 2 - boxWidth / 2;
        const y = padding + depth * (boxHeight + verticalSpacing);
        positions.set(node, { x, y });
        return nodeDim.width;
      }

      let currentX = leftX;
      node.children.forEach((child: any, index: number) => {
        const childWidth = calculatePositions(child, depth + 1, currentX);
        currentX += childWidth;
        if (index < node.children.length - 1) {
          currentX += horizontalSpacing;
        }
      });

      const firstChildPos = positions.get(node.children[0])!;
      const lastChildPos = positions.get(node.children[node.children.length - 1])!;
      const nodeX = (firstChildPos.x + lastChildPos.x + boxWidth) / 2 - boxWidth / 2;
      const nodeY = padding + depth * (boxHeight + verticalSpacing);
      positions.set(node, { x: nodeX, y: nodeY });

      return nodeDim.width;
    };

    calculatePositions(treeNode, 0, padding);

    // Draw connections
    ctx.strokeStyle = "#9E9E9E";
    ctx.lineWidth = 1;

    const drawConnections = (node: any) => {
      const parentPos = positions.get(node);
      if (!parentPos || !node.children || node.children.length === 0) return;

      const parentCenterX = parentPos.x + boxWidth / 2;
      const parentBottomY = parentPos.y + boxHeight;

      node.children.forEach((child: any) => {
        const childPos = positions.get(child);
        if (!childPos) return;

        const childCenterX = childPos.x + boxWidth / 2;
        const childTopY = childPos.y;
        const midY = (parentBottomY + childTopY) / 2;

        ctx.beginPath();
        ctx.moveTo(parentCenterX, parentBottomY);
        ctx.lineTo(parentCenterX, midY);
        ctx.lineTo(childCenterX, midY);
        ctx.lineTo(childCenterX, childTopY);
        ctx.stroke();

        drawConnections(child);
      });
    };

    drawConnections(treeNode);

    // Draw nodes
    const drawNode = (node: any) => {
      const pos = positions.get(node);
      if (!pos) return;

      // Draw box
      ctx.fillStyle = "#ffffff";
      drawRoundedRect(pos.x, pos.y, boxWidth, boxHeight, borderRadius);
      ctx.fill();

      ctx.strokeStyle = "#9E9E9E";
      ctx.lineWidth = 1;
      drawRoundedRect(pos.x, pos.y, boxWidth, boxHeight, borderRadius);
      ctx.stroke();

      // Draw text
      ctx.fillStyle = "#1e293b";
      ctx.font = "600 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const name = node.name || "Unknown";
      const maxWidth = boxWidth - 20;
      let displayName = name;

      // Truncate if too long
      while (ctx.measureText(displayName).width > maxWidth && displayName.length > 3) {
        displayName = displayName.slice(0, -4) + "...";
      }

      ctx.fillText(displayName, pos.x + boxWidth / 2, pos.y + boxHeight / 2);

      // Draw children
      if (node.children) {
        node.children.forEach((child: any) => drawNode(child));
      }
    };

    drawNode(treeNode);

    return canvas.toDataURL("image/png", 1.0);
  };

  const captureOrgChart = async (): Promise<string | null> => {
    try {
      if (!treeData) {
        toast.warn("No organization data to export", { position: "top-right" });
        return null;
      }

      // Transform treeData to D3 tree format
      const d3TreeData = Array.isArray(treeData)
        ? treeData.map(transformToD3Tree).filter(Boolean)[0]
        : transformToD3Tree(treeData);

      if (!d3TreeData) {
        toast.warn("Failed to transform tree data", { position: "top-right" });
        return null;
      }

      const base64Image = generateOrgChartImage(d3TreeData);

      if (!base64Image) {
        toast.warn("Failed to generate organization chart image", { position: "top-right" });
        return null;
      }

      return base64Image;
    } catch (error) {
      console.error("Error capturing org chart:", error);
      toast.error("Failed to capture organization chart image", { position: "top-right" });
      return null;
    }
  };



  const handleDownloadWord = async () => {
    try {
      toast.info("Preparing Export: Capturing organization chart...", { position: "top-right" });
      const orgChartImage = await captureOrgChart();
      if (!orgChartImage) {
        return;
      }

      const response = await axiosInstance.post(
        `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${currentTenant!.id}/export/docx`,
        {
          orgChartImage: orgChartImage,
          organizationId: organizationId ? Number(organizationId) : undefined,
        },
        {
          responseType: 'blob',
        }
      );

      // Handle file download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `${currentTenant!.name}-organization-structure-${Date.now()}.docx`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Word document downloaded successfully", { position: "top-right" });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to export Word document";
      toast.error(errorMessage, { position: "top-right" });
    }
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info("Preparing Export: Capturing organization chart...", { position: "top-right" });

      const orgChartImage = await captureOrgChart();
      if (!orgChartImage) {
        return;
      }

      const response = await axiosInstance.post(
        `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${currentTenant!.id}/export/pdf`,
        {
          orgChartImage: orgChartImage,
          organizationId: organizationId ? Number(organizationId) : undefined,
        },
        {
          responseType: 'blob',
        }
      );

      // Handle file download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `${currentTenant!.name}-organization-structure-${Date.now()}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("PDF document downloaded successfully", { position: "top-right" });
    } catch (error: any) {
      console.error("Error exporting PDF:", error);
      const errorMessage = error?.response?.data?.message || "Failed to export PDF document";
      toast.error(errorMessage, { position: "top-right" });
    }
  };

  const pageTitle = organizationId && orgName ? orgName : "Organization Structure";

  return (
    <section className="w-full pb-5">
      <CompanyHeader
        title={pageTitle}
        description="Define your company's structure hierarchy."
        onPublish={async (val) => {
          if (organizationId && currentTenant?.id) {
            const { setPublished } = useOrganizationListState.getState();
            await setPublished(Number(organizationId), currentTenant.id, val);
            setIsPublished(val);
          }
        }}
        isPublished={isPublished}
        showExport
        onExport={handleExport}
      />

      <div className="mx-5 border-b border-gray-200" />

      <div className="hidden md:grid grid-cols-[3fr_1fr_auto] items-center p-5">
        {viewMode !== 'form' && <h3 className="text-lg font-semibold text-slate-700">Structure</h3>}
        {viewMode !== 'diagram' && (
          <h3
            className={classNames(
              'text-lg font-semibold text-slate-700 text-left transition-all duration-300',
              viewMode === 'split' && 'pl-20',
            )}
          >
            Details
          </h3>
        )}

        <div className="flex rounded-lg border overflow-hidden bg-white shadow-sm justify-self-end">
          <button
            className={classNames('p-2', viewMode === 'split' && 'text-primary bg-active')}
            onClick={() => setViewMode('split')}
          >
            <Columns2 size={18} />
          </button>
          <button
            className={classNames('p-2', viewMode === 'diagram' && 'text-primary bg-active')}
            onClick={() => setViewMode('diagram')}
          >
            <Maximize size={18} />
          </button>
          <button
            className={classNames('p-2', viewMode === 'form' && 'text-primary bg-active')}
            onClick={() => setViewMode('form')}
          >
            <Minimize size={18} />
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row transition-all duration-300 ease-in-out px-5">
        <div
          className={classNames('transition-all duration-300 ease-in-out transform', {
            'w-full': true,
            'md:w-3/4 md:translate-x-0 md:pr-5': viewMode === 'split',
            'md:w-full': viewMode === 'diagram',
            'md:w-0 md:overflow-hidden': viewMode === 'form',
          })}
        >
          <h3 className="md:hidden text-lg font-semibold text-slate-700 mb-2">Structure</h3>

          <div ref={orgChartRef} className="bg-white rounded-lg shadow-sm p-4 border">
            <OrgChartTree
              isLoading={isLoading}
              viewMode={viewMode}
              exportMode={isExporting}
              selectedNodeId={selectedNode?.id}
              treeData={
                (() => {
                  if (!treeData) return [];
                  if (Array.isArray(treeData)) return treeData.map(transformToD3Tree).filter(Boolean);
                  return [transformToD3Tree(treeData)].filter(Boolean);
                })()
              }
              onAddChild={async (node: IOrgChart) => {
                try {
                  const orgId = node.organization_id ?? (organizationId ? Number(organizationId) : null);
                  const tenantId = useTenantState.getState().currentTenant?.id;
                  if (!tenantId) return;

                  const newNode = await useOrgChartState.getState().createChildOrgChart(node.id, {
                    name: `Position #${positionCounter.current++}`,
                    description: '',
                    is_published: true,
                    organization_id: orgId
                  });

                  const newTree = await useOrgChartState.getState().fetchOrgChartTree(tenantId, organizationId ? Number(organizationId) : undefined);
                  setTreeData(newTree);

                  // Open edit form for the new node
                  if (newNode) {
                    setSelectedNode(newNode);
                    setFormMode('edit');
                    setAutoFocusForm(true);
                    setViewMode(prev => (prev === 'diagram' ? 'form' : 'split'));
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
              onAddLeftSibling={async (node: IOrgChart) => {
                try {
                  const orgId = node.organization_id ?? (organizationId ? Number(organizationId) : null);
                  const tenantId = useTenantState.getState().currentTenant?.id;
                  if (!tenantId) return;

                  const newNode = await useOrgChartState.getState().createSiblingOrgChart(node.id, {
                    name: `Position #${positionCounter.current++}`,
                    description: '',
                    is_published: true,
                    organization_id: orgId
                  }, 'before');

                  const newTree = await useOrgChartState.getState().fetchOrgChartTree(tenantId, organizationId ? Number(organizationId) : undefined);
                  setTreeData(newTree);

                  // Open edit form for the new node
                  if (newNode) {
                    setSelectedNode(newNode);
                    setFormMode('edit');
                    setAutoFocusForm(true);
                    setViewMode(prev => (prev === 'diagram' ? 'form' : 'split'));
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
              onAddRightSibling={async (node: IOrgChart) => {
                try {
                  const orgId = node.organization_id ?? (organizationId ? Number(organizationId) : null);
                  const tenantId = useTenantState.getState().currentTenant?.id;
                  if (!tenantId) return;

                  const newNode = await useOrgChartState.getState().createSiblingOrgChart(node.id, {
                    name: `Position #${positionCounter.current++}`,
                    description: '',
                    is_published: true,
                    organization_id: orgId
                  }, 'after');

                  const newTree = await useOrgChartState.getState().fetchOrgChartTree(tenantId, organizationId ? Number(organizationId) : undefined);
                  setTreeData(newTree);

                  // Open edit form for the new node
                  if (newNode) {
                    setSelectedNode(newNode);
                    setFormMode('edit');
                    setAutoFocusForm(true);
                    setViewMode(prev => (prev === 'diagram' ? 'form' : 'split'));
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
              onAddTopNode={async (node: IOrgChart) => {
                try {
                  const orgId = node.organization_id ?? (organizationId ? Number(organizationId) : null);
                  const tenantId = useTenantState.getState().currentTenant?.id;
                  if (!tenantId) return;

                  // Insert a new parent node above the current root node
                  const newNode = await useOrgChartState.getState().insertAboveOrgChart(node.id, {
                    name: `Position #${positionCounter.current++}`,
                    description: '',
                    is_published: true,
                    organization_id: orgId
                  });

                  const newTree = await useOrgChartState.getState().fetchOrgChartTree(tenantId, organizationId ? Number(organizationId) : undefined);
                  setTreeData(newTree);

                  // Open edit form for the new node
                  if (newNode) {
                    setSelectedNode(newNode);
                    setFormMode('edit');
                    setAutoFocusForm(true);
                    setViewMode(prev => (prev === 'diagram' ? 'form' : 'split'));
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
              onEdit={(node: IOrgChart) => {
                setFormMode('edit');
                setSelectedNode(node);
                setAutoFocusForm(false);
                setViewMode(prev => (prev === 'diagram' ? 'form' : 'split'));
              }}
              onSelect={(node: IOrgChart) => {
                if (formMode === 'edit') {
                  if (selectedNode?.id === node.id) {
                    setFormMode(null);
                    setViewMode('split');
                    return;
                  }
                  setFormMode(null);
                  setSelectedNode(node);
                  setViewMode('split');
                  return;
                }
                setSelectedNode(node);
                setViewMode('split');
              }}
              onDelete={async (node: IOrgChart) => {
                try {
                  await useOrgChartState.getState().deleteOrgChart(node.id);
                  const tenantId = useTenantState.getState().currentTenant?.id;
                  if (!tenantId) return;
                  const newTree = await useOrgChartState.getState().fetchOrgChartTree(tenantId, organizationId ? Number(organizationId) : undefined);
                  setTreeData(newTree);
                  if (selectedNode?.id === node.id) {
                    setSelectedNode(null);
                  }
                } catch (err) {
                  console.error('Error deleting org chart:', err);
                }
              }}
            />
          </div>
        </div>

        <div
          className={classNames('transition-all duration-300 ease-in-out transform', {
            'w-full': true,
            'md:w-1/4': viewMode === 'split',
            'md:w-full': viewMode === 'form',
            'md:w-0 md:overflow-hidden': viewMode === 'diagram',
          })}
        >
          <h3 className="md:hidden text-lg font-semibold text-slate-700 mb-2 mt-4">Details</h3>

          {formMode && viewMode !== 'diagram' ? (
            <OrgChartForm
              formMode={formMode}
              selectedNode={selectedNode}
              onSave={handleSave}
              autoFocus={autoFocusForm}
              onCancel={() => {
                setFormMode(null);
                setAutoFocusForm(false);
                setViewMode('split');
              }}
            />
          ) : (
            <OrgChartDetails selectedNode={selectedNode} />
          )}
        </div>
      </div>

    </section>
  );
}
